// Skizzen-Modus (Shapr3D-inspiriert): 2D-Formen auf der Bodenebene zeichnen
// und zu Teilen extrudieren. Rechtecke werden normale Platten; Kreise und
// Polygone werden Formteile mit freier Außenkontur (meta.outline). Engine-frei.

const MIN_SIZE = 10
const CIRCLE_SEGMENTS = 32
export const POLY_CLOSE_MM = 40 // Tipp-Toleranz zum Schließen auf den Startpunkt

export function rectShape(a, b) {
  return { kind: "rect", ax: a.x, az: a.z, bx: b.x, bz: b.z }
}

export function circleShape(center, edge) {
  return { kind: "circle", cx: center.x, cz: center.z, r: Math.hypot(edge.x - center.x, edge.z - center.z) }
}

export function polygonShape(points) {
  return { kind: "poly", points: points.map((p) => [ p[0], p[1] ]) }
}

// --- Eckradien (Bögen): poly-Punkte dürfen optional [x, z, r] sein ---------
//
// r > 0 rundet die Ecke mit einem tangentialen Kreisbogen ab; fehlendes
// drittes Element oder 0 bedeutet scharfe Ecke (abwärtskompatibel zu [x, z]).
// Auswahl/Maße arbeiten weiter auf den logischen Stützpunkten; nur die
// Kontur-Erzeugung (Outline, Darstellung) rollt die Bögen aus.

const ARC_STEP_DEG = 12
const MIN_ARC_SEGMENTS = 3

export function cornerRadius(shape, cornerIndex) {
  if (shape.kind !== "poly") return 0
  return shape.points[cornerIndex]?.[2] || 0
}

// Größter machbarer Radius an einer Ecke: der Tangentenabstand darf höchstens
// die halbe Länge der kürzeren Nachbarkante betragen
function maxCornerRadius(points, cornerIndex) {
  const n = points.length
  const cur = points[cornerIndex]
  const prev = points[(cornerIndex - 1 + n) % n]
  const next = points[(cornerIndex + 1) % n]
  const lenU = Math.hypot(prev[0] - cur[0], prev[1] - cur[1])
  const lenW = Math.hypot(next[0] - cur[0], next[1] - cur[1])
  if (lenU === 0 || lenW === 0) return 0
  const ux = (prev[0] - cur[0]) / lenU, uz = (prev[1] - cur[1]) / lenU
  const wx = (next[0] - cur[0]) / lenW, wz = (next[1] - cur[1]) / lenW
  const theta = Math.acos(Math.max(-1, Math.min(1, ux * wx + uz * wz)))
  if (theta <= 1e-6 || theta >= Math.PI - 1e-6) return 0 // entartet/gestreckt
  return (Math.min(lenU, lenW) / 2) * Math.tan(theta / 2)
}

// Neues poly-Shape mit gesetztem Eckradius (geklemmt); radius ≤ 0 entfernt ihn
export function withCornerRadius(shape, cornerIndex, radiusMm) {
  if (shape.kind !== "poly" || !shape.points[cornerIndex]) return shape
  const points = shape.points.map((pt) => [ ...pt ])
  // Rundung ist unkritisch: die Tessellation klemmt den Tangentenabstand erneut
  const radius = Math.min(Math.max(0, Math.round(radiusMm)), Math.round(maxCornerRadius(points, cornerIndex)))
  points[cornerIndex] = radius > 0
    ? [ points[cornerIndex][0], points[cornerIndex][1], radius ]
    : [ points[cornerIndex][0], points[cornerIndex][1] ]
  return { ...shape, points }
}

// Eine Ecke ausrollen: scharf → der Punkt selbst; mit Radius → tangentialer
// Kreisbogen-Fillet (Tangentenpunkte auf beiden Nachbarkanten)
function filletCorner(prev, cur, next) {
  const sharp = [ [ cur[0], cur[1] ] ]
  const r = cur[2] || 0
  if (r <= 0) return sharp
  const lenU = Math.hypot(prev[0] - cur[0], prev[1] - cur[1])
  const lenW = Math.hypot(next[0] - cur[0], next[1] - cur[1])
  if (lenU === 0 || lenW === 0) return sharp
  const ux = (prev[0] - cur[0]) / lenU, uz = (prev[1] - cur[1]) / lenU
  const wx = (next[0] - cur[0]) / lenW, wz = (next[1] - cur[1]) / lenW
  const theta = Math.acos(Math.max(-1, Math.min(1, ux * wx + uz * wz)))
  if (theta <= 1e-6 || theta >= Math.PI - 1e-6) return sharp
  const tanHalf = Math.tan(theta / 2)
  const radius = Math.min(r, (Math.min(lenU, lenW) / 2) * tanHalf)
  const t = radius / tanHalf // Tangentenabstand entlang beider Kanten
  const t1 = [ cur[0] + ux * t, cur[1] + uz * t ]
  const t2 = [ cur[0] + wx * t, cur[1] + wz * t ]
  // Bogenzentrum liegt auf der Winkelhalbierenden
  const bx = ux + wx, bz = uz + wz
  const bLen = Math.hypot(bx, bz)
  const d = radius / Math.sin(theta / 2)
  const cx = cur[0] + (bx / bLen) * d
  const cz = cur[1] + (bz / bLen) * d
  const a1 = Math.atan2(t1[1] - cz, t1[0] - cx)
  const a2 = Math.atan2(t2[1] - cz, t2[0] - cx)
  let sweep = a2 - a1 // Bogenwinkel = π − theta < π → kürzere Richtung stimmt
  if (sweep > Math.PI) sweep -= 2 * Math.PI
  if (sweep < -Math.PI) sweep += 2 * Math.PI
  const steps = Math.max(MIN_ARC_SEGMENTS, Math.ceil(Math.abs(sweep) / ((ARC_STEP_DEG * Math.PI) / 180)))
  return Array.from({ length: steps + 1 }, (_, i) => {
    const a = a1 + (sweep * i) / steps
    return [ cx + Math.cos(a) * radius, cz + Math.sin(a) * radius ]
  })
}

// Ausgerollte Punktliste eines Shapes: poly-Ecken mit Radius werden zu Bögen,
// alle anderen Formen liefern ihre bisherigen Punktlisten
export function tessellatedPoints(shape) {
  if (shape.kind === "rect") return shapeCorners(shape)
  if (shape.kind === "circle") {
    return Array.from({ length: CIRCLE_SEGMENTS }, (_, i) => {
      const a = (i / CIRCLE_SEGMENTS) * Math.PI * 2
      return [ shape.cx + Math.cos(a) * shape.r, shape.cz + Math.sin(a) * shape.r ]
    })
  }
  if (shape.kind === "line") return [ [ shape.ax, shape.az ], [ shape.bx, shape.bz ] ]
  const pts = shape.points
  const n = pts.length
  return pts.flatMap((cur, i) => filletCorner(pts[(i - 1 + n) % n], cur, pts[(i + 1) % n]))
}

// Konstruktionslinie: reine Hilfsgeometrie, wird nie extrudiert,
// liefert aber Fangpunkte (Endpunkte + Mitte) und ist maß-editierbar
export function lineShape(a, b) {
  return { kind: "line", ax: a.x, az: a.z, bx: b.x, bz: b.z }
}

export function isConstruction(shape) {
  return shape.kind === "line"
}

export function shapeIsValid(shape) {
  if (shape.kind === "rect") return Math.abs(shape.bx - shape.ax) >= MIN_SIZE && Math.abs(shape.bz - shape.az) >= MIN_SIZE
  if (shape.kind === "circle") return shape.r * 2 >= MIN_SIZE
  if (shape.kind === "line") return Math.hypot(shape.bx - shape.ax, shape.bz - shape.az) >= MIN_SIZE
  return shape.points.length >= 3 && bounds(shape.points).sizeX >= MIN_SIZE && bounds(shape.points).sizeZ >= MIN_SIZE
}

// --- Referenzebene: achsparallele Ebene, auf der die Skizze liegt ----------
//
// plane = { normal: "y+"|"y-"|"x+"|"x-"|"z+"|"z-", offset: mm }.
// Skizzen-Koordinaten (u, v) — im Shape weiterhin als (x, z) benannt —
// werden je Ebene auf die Welt abgebildet:
//   y±: u→Welt-x, v→Welt-z (waagerecht, wie bisher)
//   z±: u→Welt-x, v→Welt-y (Front/Rückseite)
//   x±: u→Welt-z, v→Welt-y (Seiten)
// Extrudiert wird entlang der Normalen (aus der Fläche heraus).

export const PLANE_DEFAULT = { normal: "y+", offset: 0 }

// Versteht alle historischen Formate: Zahl (v2-planeY) und fehlend
export function normalizePlane(raw) {
  if (typeof raw === "number") return { normal: "y+", offset: raw }
  if (!raw || typeof raw.normal !== "string") return { ...PLANE_DEFAULT }
  return { normal: raw.normal, offset: Number(raw.offset) || 0 }
}

export function planeToWorld(plane, u, v) {
  const { normal, offset } = normalizePlane(plane)
  if (normal[0] === "y") return [ u, offset, v ]
  if (normal[0] === "z") return [ u, v, offset ]
  return [ offset, v, u ]
}

export function worldToPlane(plane, [ x, y, z ]) {
  const { normal } = normalizePlane(plane)
  if (normal[0] === "y") return { x, z }
  if (normal[0] === "z") return { x, z: y }
  return { x: z, z: y }
}

// Ebene aus einem Oberflächen-Treffer: dominante Normalen-Achse entscheidet
export function planeFromHit(point, normal) {
  const ax = Math.abs(normal.x), ay = Math.abs(normal.y), az = Math.abs(normal.z)
  if (ay >= ax && ay >= az) return { normal: normal.y >= 0 ? "y+" : "y-", offset: Math.round(point.y) }
  if (ax >= az) return { normal: normal.x >= 0 ? "x+" : "x-", offset: Math.round(point.x) }
  return { normal: normal.z >= 0 ? "z+" : "z-", offset: Math.round(point.z) }
}

// Positionsfeld eines Teils entlang der Ebenen-Normalachse — für die
// Kopplung einer Skizzier-Ebene an ein Teil (plane.ref)
export function planeAxisField(plane) {
  return { x: "pos_x", y: "pos_y", z: "pos_z" }[normalizePlane(plane).normal[0]]
}

export function viewForPlane(plane) {
  return { "y+": "top", "y-": "top", "z-": "front", "z+": "back", "x+": "right", "x-": "left" }[normalizePlane(plane).normal]
}

export function planeLabel(plane) {
  return {
    "y+": "Oben", "y-": "Unten", "z-": "Vorn", "z+": "Hinten", "x+": "Rechts", "x-": "Links"
  }[normalizePlane(plane).normal]
}

// Extrusion: liefert Attribut-Objekt für newPart (Teil sitzt mit der
// Unterseite auf der Skizzen-Ebene, Position = Konturmittelpunkt).
// Auf senkrechten Ebenen entsteht ein entsprechend rotiertes Teil.
export function shapeToPartAttrs(shape, thicknessMm, plane = PLANE_DEFAULT) {
  const thickness = Math.max(1, Math.round(thicknessMm))
  if (isConstruction(shape) || !shapeIsValid(shape)) return null
  const { normal, offset } = normalizePlane(plane)
  const sign = normal[1] === "+" ? 1 : -1

  // 2D-Kennwerte in Ebenen-Koordinaten (u, v)
  let name, cu, cv, sizeU, sizeV, pointsUV = null
  if (shape.kind === "rect") {
    name = "Platte"
    cu = (shape.ax + shape.bx) / 2
    cv = (shape.az + shape.bz) / 2
    sizeU = Math.abs(shape.bx - shape.ax)
    sizeV = Math.abs(shape.bz - shape.az)
  } else {
    name = shape.kind === "circle" ? "Rundteil" : "Formteil"
    pointsUV = tessellatedPoints(shape) // rollt poly-Eckradien als Bögen aus
    const box = bounds(pointsUV)
    cu = box.centerX
    cv = box.centerZ
    sizeU = box.sizeX
    sizeV = box.sizeZ
  }

  // Kontur in Teil-lokale (x, z) übersetzen — abhängig von der Teil-Rotation
  // je Ebene (x±: lokal x = v, lokal z = u; sonst lokal = (u, v))
  const outlineOf = (mapPoint) => pointsUV && ensureWinding(pointsUV.map((p) => {
    const [ lx, lz ] = mapPoint(p)
    return [ Math.round(lx * 10) / 10, Math.round(lz * 10) / 10 ]
  }))

  const base = { name, thickness_mm: thickness }
  const dims = (length, width) => ({
    length_mm: Math.max(MIN_SIZE, Math.round(length)), width_mm: Math.max(MIN_SIZE, Math.round(width))
  })
  const withOutline = (attrs, outline) => ({ ...attrs, meta: outline ? { outline } : {} })
  // Positionen weg von Null runden (wie der Korpus-Generator) — sonst
  // runden Vorder- und Rückseite bei halben Millimetern unsymmetrisch
  const roundPos = (v) => Math.sign(v) * Math.round(Math.abs(v))
  const center = offset + sign * (thickness / 2)

  if (normal[0] === "y") {
    return withOutline({
      ...base, ...dims(sizeU, sizeV),
      pos_x: roundPos(cu), pos_y: roundPos(center), pos_z: roundPos(cv)
    }, outlineOf(([ u, v ]) => [ u - cu, v - cv ]))
  }
  if (normal[0] === "z") {
    // rot_x −90: lokal x → Welt-x, lokal z → Welt-y
    return withOutline({
      ...base, ...dims(sizeU, sizeV), rot_x: -90,
      pos_x: roundPos(cu), pos_y: roundPos(cv), pos_z: roundPos(center)
    }, outlineOf(([ u, v ]) => [ u - cu, v - cv ]))
  }
  // rot_z 90: lokal x → Welt-y, lokal z → Welt-z
  return withOutline({
    ...base, ...dims(sizeV, sizeU), rot_z: 90,
    pos_x: roundPos(center), pos_y: roundPos(cv), pos_z: roundPos(cu)
  }, outlineOf(([ u, v ]) => [ v - cv, u - cu ]))
}

// --- Auswahl in der Skizze: Kanten treffen, Flächen treffen, Maß setzen ----

// Eckpunkte eines Shapes als geschlossener Umlauf (Kreis hat keine Eckpunkte)
export function shapeCorners(shape) {
  if (shape.kind === "rect") {
    return [ [ shape.ax, shape.az ], [ shape.bx, shape.az ], [ shape.bx, shape.bz ], [ shape.ax, shape.bz ] ]
  }
  if (shape.kind === "poly") return shape.points
  return []
}

// Kante i = corners[i] → corners[i+1]; beim Kreis steht -1 für die Kreislinie
export function edgeLength(shape, edgeIndex) {
  if (shape.kind === "circle") return shape.r * 2 // Durchmesser
  if (shape.kind === "line") return Math.hypot(shape.bx - shape.ax, shape.bz - shape.az)
  const corners = shapeCorners(shape)
  const [ x1, z1 ] = corners[edgeIndex]
  const [ x2, z2 ] = corners[(edgeIndex + 1) % corners.length]
  return Math.hypot(x2 - x1, z2 - z1)
}

function segmentDistance(p, a, b) {
  const dx = b[0] - a[0], dz = b[1] - a[1]
  const lenSq = dx * dx + dz * dz
  const t = lenSq === 0 ? 0 : Math.max(0, Math.min(1, ((p.x - a[0]) * dx + (p.z - a[1]) * dz) / lenSq))
  return Math.hypot(p.x - (a[0] + t * dx), p.z - (a[1] + t * dz))
}

// Nächste Kante unter dem Finger (Toleranz in mm, zoomabhängig vom Aufrufer)
export function hitEdge(shapes, p, tolerance) {
  let best = null
  shapes.forEach((shape, shapeIndex) => {
    if (shape.kind === "circle") {
      const dist = Math.abs(Math.hypot(p.x - shape.cx, p.z - shape.cz) - shape.r)
      if (dist <= tolerance && (!best || dist < best.dist)) best = { shapeIndex, edgeIndex: -1, dist }
      return
    }
    if (shape.kind === "line") {
      const dist = segmentDistance(p, [ shape.ax, shape.az ], [ shape.bx, shape.bz ])
      if (dist <= tolerance && (!best || dist < best.dist)) best = { shapeIndex, edgeIndex: 0, dist }
      return
    }
    const corners = shapeCorners(shape)
    corners.forEach((corner, edgeIndex) => {
      const dist = segmentDistance(p, corner, corners[(edgeIndex + 1) % corners.length])
      if (dist <= tolerance && (!best || dist < best.dist)) best = { shapeIndex, edgeIndex, dist }
    })
  })
  return best
}

// Nächster poly-Stützpunkt unter dem Finger — für das Setzen von Eckradien.
// Nur logische Ecken (keine tessellierten Bogenpunkte), nur Polygone.
export function hitCorner(shapes, p, tolerance) {
  let best = null
  shapes.forEach((shape, shapeIndex) => {
    if (shape.kind !== "poly") return
    shape.points.forEach(([ x, z ], cornerIndex) => {
      const dist = Math.hypot(p.x - x, p.z - z)
      if (dist <= tolerance && (!best || dist < best.dist)) best = { shapeIndex, cornerIndex, dist }
    })
  })
  return best
}

export function pointInShape(shape, p) {
  if (shape.kind === "line") return false
  if (shape.kind === "rect") {
    return p.x >= Math.min(shape.ax, shape.bx) && p.x <= Math.max(shape.ax, shape.bx) &&
           p.z >= Math.min(shape.az, shape.bz) && p.z <= Math.max(shape.az, shape.bz)
  }
  if (shape.kind === "circle") return Math.hypot(p.x - shape.cx, p.z - shape.cz) <= shape.r
  // Ray-Casting fürs Polygon
  let inside = false
  const pts = shape.points
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const [ xi, zi ] = pts[i]
    const [ xj, zj ] = pts[j]
    if ((zi > p.z) !== (zj > p.z) && p.x < ((xj - xi) * (p.z - zi)) / (zj - zi) + xi) inside = !inside
  }
  return inside
}

// Oberste getroffene Fläche (zuletzt gezeichnete gewinnt)
export function hitFace(shapes, p) {
  for (let i = shapes.length - 1; i >= 0; i--) {
    if (pointInShape(shapes[i], p)) return i
  }
  return null
}

// --- Gesperrte Maße: shape.locked = [edgeIndex, …] --------------------------
//
// Eine gesperrte Kante behält ihr Maß: withEdgeLength lehnt jede Änderung ab,
// die die Dimension einer gesperrten Kante berühren würde. Beim Kreis steht
// der Eintrag 0 für den Durchmesser (die Kreislinie meldet edgeIndex -1).

function lockKey(shape, edgeIndex) {
  return shape.kind === "circle" ? 0 : edgeIndex
}

export function isEdgeLocked(shape, edgeIndex) {
  return (shape.locked ?? []).includes(lockKey(shape, edgeIndex))
}

// Sperre umschalten: liefert ein NEUES Shape; ohne Sperren fällt das
// locked-Feld weg (hält das persistierte sketch_state schlank)
export function withEdgeLockToggled(shape, edgeIndex) {
  const key = lockKey(shape, edgeIndex)
  const locked = (shape.locked ?? []).includes(key)
    ? shape.locked.filter((i) => i !== key)
    : [ ...(shape.locked ?? []), key ]
  const next = { ...shape, locked }
  if (locked.length === 0) delete next.locked
  return next
}

// Würde eine Maßänderung an edgeIndex eine gesperrte Kante verändern?
// rect: parallele Kanten teilen sich die Dimension; poly: der verschobene
// Endpunkt gehört auch zur Folgekante; circle/line haben nur ein Maß.
function lockBlocksEdit(shape, edgeIndex) {
  const locked = shape.locked ?? []
  if (locked.length === 0) return false
  if (shape.kind === "circle" || shape.kind === "line") return locked.includes(0)
  if (shape.kind === "rect") return locked.some((i) => i % 2 === edgeIndex % 2)
  const n = shape.points.length
  return locked.includes(edgeIndex) || locked.includes((edgeIndex + 1) % n)
}

// Kantenmaß setzen: liefert ein NEUES Shape.
// Rechteck: Kantenlänge = Seitenmaß; die min-Seite bleibt stehen, max wächst.
// Polygon: der Endpunkt der Kante wandert entlang der Kantenrichtung.
// Kreis: Maß = Durchmesser. Gesperrte Kanten bleiben unangetastet.
export function withEdgeLength(shape, edgeIndex, lengthMm) {
  if (lockBlocksEdit(shape, edgeIndex)) return shape
  const len = Math.max(MIN_SIZE, Math.round(lengthMm))
  if (shape.kind === "circle") return { ...shape, r: len / 2 }

  if (shape.kind === "line") {
    const current = Math.hypot(shape.bx - shape.ax, shape.bz - shape.az)
    if (current === 0) return shape
    const scale = len / current
    return {
      ...shape,
      bx: Math.round(shape.ax + (shape.bx - shape.ax) * scale),
      bz: Math.round(shape.az + (shape.bz - shape.az) * scale)
    }
  }

  if (shape.kind === "rect") {
    const minX = Math.min(shape.ax, shape.bx), minZ = Math.min(shape.az, shape.bz)
    const maxX = Math.max(shape.ax, shape.bx), maxZ = Math.max(shape.az, shape.bz)
    const horizontal = edgeIndex % 2 === 0 // Kanten 0/2 laufen in x, 1/3 in z
    return horizontal
      ? { ...shape, ax: minX, bx: minX + len, az: minZ, bz: maxZ }
      : { ...shape, ax: minX, bx: maxX, az: minZ, bz: minZ + len }
  }

  const points = shape.points.map((pt) => [ ...pt ])
  const a = points[edgeIndex]
  const b = points[(edgeIndex + 1) % points.length]
  const current = Math.hypot(b[0] - a[0], b[1] - a[1])
  if (current === 0) return shape
  const scale = len / current
  points[(edgeIndex + 1) % points.length] = [
    Math.round(a[0] + (b[0] - a[0]) * scale),
    Math.round(a[1] + (b[1] - a[1]) * scale),
    ...(b[2] ? [ b[2] ] : []) // Eckradius des verschobenen Punkts bleibt erhalten
  ]
  return { ...shape, points }
}

export function nearFirstPoint(points, p) {
  if (points.length === 0) return false
  const [ x, z ] = points[0]
  return Math.hypot(p.x - x, p.z - z) <= POLY_CLOSE_MM
}

function bounds(points) {
  const xs = points.map((p) => p[0])
  const zs = points.map((p) => p[1])
  const minX = Math.min(...xs), maxX = Math.max(...xs)
  const minZ = Math.min(...zs), maxZ = Math.max(...zs)
  return {
    sizeX: maxX - minX, sizeZ: maxZ - minZ,
    centerX: (minX + maxX) / 2, centerZ: (minZ + maxZ) / 2
  }
}

// Kontur-Windung normalisieren: in Shape-Koordinaten (x, -z) muss sie CCW
// sein (Shoelace über (x,z) also negativ), sonst kippt die CSG-Füllregel
function ensureWinding(points) {
  let area = 0
  for (let i = 0; i < points.length; i++) {
    const [ x1, z1 ] = points[i]
    const [ x2, z2 ] = points[(i + 1) % points.length]
    area += x1 * z2 - x2 * z1
  }
  return area > 0 ? [ ...points ].reverse() : points
}

// --- Fangen: Referenzpunkte und Snapping -----------------------------------

// Referenzpunkte aller Shapes: Ecken, Kantenmitten, Zentren, Linien-Enden.
// excludeIndex blendet das gerade gezeichnete/bearbeitete Shape aus.
export function sketchReferencePoints(shapes, excludeIndex = -1) {
  const refs = []
  shapes.forEach((shape, index) => {
    if (index === excludeIndex) return
    if (shape.kind === "circle") {
      refs.push({ x: shape.cx, z: shape.cz, kind: "center" })
      for (const [ dx, dz ] of [ [ 1, 0 ], [ -1, 0 ], [ 0, 1 ], [ 0, -1 ] ]) {
        refs.push({ x: shape.cx + dx * shape.r, z: shape.cz + dz * shape.r, kind: "quadrant" })
      }
      return
    }
    if (shape.kind === "line") {
      refs.push({ x: shape.ax, z: shape.az, kind: "end" })
      refs.push({ x: shape.bx, z: shape.bz, kind: "end" })
      refs.push({ x: (shape.ax + shape.bx) / 2, z: (shape.az + shape.bz) / 2, kind: "mid" })
      return
    }
    const corners = shapeCorners(shape)
    corners.forEach((corner, i) => {
      const next = corners[(i + 1) % corners.length]
      refs.push({ x: corner[0], z: corner[1], kind: "corner" })
      refs.push({ x: (corner[0] + next[0]) / 2, z: (corner[1] + next[1]) / 2, kind: "mid" })
    })
    if (shape.kind === "rect") {
      refs.push({ x: (shape.ax + shape.bx) / 2, z: (shape.az + shape.bz) / 2, kind: "center" })
    }
  })
  return refs
}

// Fangender Punkt: 1) exakter Referenzpunkt, 2) Achsen-Fang (gleiches x
// bzw. z wie ein Referenzpunkt), 3) Raster. Liefert Snap-Info fürs Feedback.
export function snapSketchPoint(p, { refs = [], gridMm = 10, tolerance = 15 } = {}) {
  let bestPoint = null
  for (const ref of refs) {
    const dist = Math.hypot(p.x - ref.x, p.z - ref.z)
    if (dist <= tolerance && (!bestPoint || dist < bestPoint.dist)) bestPoint = { ref, dist }
  }
  if (bestPoint) {
    return { x: bestPoint.ref.x + 0, z: bestPoint.ref.z + 0, snap: { type: "point", ref: bestPoint.ref } }
  }

  let bestX = null
  let bestZ = null
  for (const ref of refs) {
    const dx = Math.abs(p.x - ref.x)
    const dz = Math.abs(p.z - ref.z)
    if (dx <= tolerance && (!bestX || dx < bestX.dist)) bestX = { ref, dist: dx }
    if (dz <= tolerance && (!bestZ || dz < bestZ.dist)) bestZ = { ref, dist: dz }
  }
  const x = bestX ? bestX.ref.x : snapToGridMm(p.x, gridMm)
  const z = bestZ ? bestZ.ref.z : snapToGridMm(p.z, gridMm)
  const snap = bestX || bestZ ? { type: "axis", refX: bestX?.ref ?? null, refZ: bestZ?.ref ?? null } : null
  return { x: x + 0, z: z + 0, snap }
}

function snapToGridMm(value, gridMm) {
  // + 0 macht aus -0 (Rundung knapp unter null) eine saubere 0
  return Math.round(value / gridMm) * gridMm + 0
}
